import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { CardButtonContent } from './design-system/CardButtonContent';
import {
  walletTemplates,
  categoryColors,
  WalletTemplate,
} from 'data/shared/walletTemplates';
import { Link } from 'react-router-dom';
import { memo, useState } from 'react';
import { CategoryEarnRatePill } from './CreditPills';
import { flattenRewardsEarnRates } from 'data/shared/utils/flattenRewardsEarnRates';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';

const generateWalletUrl = (wallet: WalletTemplate): string => {
  return `${window.location.origin}/w/${wallet.id}`;
};

/**
 * Gets the top earn rate pills for a wallet template
 */
function getWalletEarnRatePills(wallet: WalletTemplate): Array<{
  category: string;
  earnRate: number;
  rewardsType: CreditCardRewardsType;
}> {
  const rateMap = new Map<
    string,
    { rate: number; rewardsType: CreditCardRewardsType }
  >();

  wallet.cards.forEach((cardId) => {
    const cardData = CreditCardDataFiltered[cardId];
    if (!cardData?.rewards_earn_rates) {
      return;
    }

    // Filter out rates that require travel portal (consistent with table logic)
    const filteredRates = cardData.rewards_earn_rates.filter(
      (rate) => !rate.requiresTravelPortal
    );

    // Flatten earn rates to handle selectable categories, quarterly categories, etc.
    const flattenedRates = flattenRewardsEarnRates(
      filteredRates,
      undefined, // No user selections for wallet templates
      cardData.rewardsType
    );

    flattenedRates.forEach((rate) => {
      if (!rate.category || !rate.earnRate) {
        return;
      }

      const current = rateMap.get(rate.category);
      // Take the maximum rate for each category
      if (!current || rate.earnRate > current.rate) {
        rateMap.set(rate.category, {
          rate: rate.earnRate,
          rewardsType: (cardData.rewardsType ||
            'cash_back') as CreditCardRewardsType,
        });
      }
    });
  });

  // Get category depth for sorting
  const getCategoryDepth = (category: string): number => {
    return category.split('.').length;
  };

  return Array.from(rateMap.entries())
    .map(([category, { rate, rewardsType }]) => ({
      category,
      earnRate: rate,
      rewardsType,
      depth: getCategoryDepth(category),
    }))
    .filter((item) => item.earnRate > 1) // Only show rates > 1%
    .sort((a, b) => {
      // Prioritize categories with depth 1 or 2
      const depthA = a.depth <= 2 ? 1 : 0;
      const depthB = b.depth <= 2 ? 1 : 0;

      // If one is shallow (1-2 levels) and the other is not, prioritize the shallow one
      if (depthA !== depthB) {
        return depthB - depthA;
      }

      // Otherwise, sort by rate
      return b.earnRate - a.earnRate;
    })
    .slice(0, 4) // Show top 4 earn rates
    .map(({ category, earnRate, rewardsType }) => ({
      category,
      earnRate,
      rewardsType,
    }));
}

// Memoized wallet card component to prevent unnecessary re-renders
const WalletCard = memo(
  ({
    wallet,
    hasCardsSelected,
    onClick,
    isMobile,
  }: {
    wallet: WalletTemplate;
    hasCardsSelected: boolean;
    onClick: () => void;
    isMobile: boolean;
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Filter out any cards that don't exist in the data
    const validCards = wallet.cards.filter(
      (card) => CreditCardDataFiltered[card]
    );

    if (validCards.length === 0) {
      return null;
    }

    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          minWidth: '320px',
          maxWidth: '320px',
          width: '320px',
          flexShrink: 0,
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          padding: 24,
          cursor: 'pointer',
          backgroundColor: 'white',
          scrollSnapAlign: 'start',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          willChange: 'transform',
          contain: 'layout style paint',
          overflow: 'hidden',
          boxSizing: 'border-box',
          boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
          transform: isHovered
            ? 'translate3d(0, -2px, 0)'
            : 'translate3d(0, 0, 0)',
        }}
      >
        {/* Category Badge and Icon */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            marginBottom: '12px',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              color: categoryColors[wallet.category],
              backgroundColor: `${categoryColors[wallet.category]}15`,
              padding: '4px 12px',
              borderRadius: '12px',
            }}
          >
            {wallet.category}
          </span>
          {hasCardsSelected ? (
            // Open in new tab icon
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0E80D5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          ) : (
            // Arrow up icon for same-page navigation
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0E80D5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          )}
        </div>

        {/* Wallet Name */}
        <h3
          style={{
            fontFamily: 'Merriweather',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            color: '#1a1a1a',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          {wallet.name}
        </h3>

        {/* Wallet Description */}
        <p
          style={{
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.5',
            margin: '0 0 12px 0',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {wallet.editorsNote}
        </p>

        {/* Wallet Details */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '16px',
            fontSize: '13px',
          }}
        >
          {/* <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
            }}
          >
            <span style={{ color: '#999', fontWeight: 500, flexShrink: 0 }}>
              Complexity:
            </span>
            <span style={{ color: '#333', overflow: 'hidden' }}>
              {'⭐'.repeat(wallet.complexity)}{' '}
              {wallet.complexity === 1 && '(Very Easy)'}
              {wallet.complexity === 2 && '(Easy)'}
              {wallet.complexity === 3 && '(Moderate)'}
              {wallet.complexity === 4 && '(Advanced)'}
              {wallet.complexity === 5 && '(Expert)'}
            </span>
          </div> */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
            }}
          >
            <span style={{ color: '#999', fontWeight: 500, flexShrink: 0 }}>
              Total Annual Fees:
            </span>
            <span
              style={{
                color: '#333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {wallet.annualFees}
            </span>
          </div>
        </div>

        {/* Earn Rate Pills */}
        {(() => {
          const earnRatePills = getWalletEarnRatePills(wallet);
          if (earnRatePills.length === 0) {
            return null;
          }
          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '16px',
              }}
            >
              {earnRatePills.map((pill, index) => (
                <CategoryEarnRatePill
                  key={`${pill.category}-${index}`}
                  category={pill.category}
                  earnRate={pill.earnRate}
                  rewardsType={pill.rewardsType}
                />
              ))}
            </div>
          );
        })()}

        {/* Cards in Wallet */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            flexWrap: 'wrap',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {validCards.map((cardId) => {
            const cardData = CreditCardDataFiltered[cardId];
            if (!cardData) {
              return null;
            }

            return (
              <div key={cardId} style={{ marginBottom: '4px', flexShrink: 0 }}>
                <CardButtonContent
                  cardData={cardData}
                  size="small"
                  hideName={false}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

WalletCard.displayName = 'WalletCard';

export const ExploreWallets = ({
  hasCardsSelected,
  isMobile,
}: {
  hasCardsSelected: boolean;
  isMobile: boolean;
}) => {
  const handleWalletClick = (wallet: WalletTemplate) => {
    const url = generateWalletUrl(wallet);

    if (hasCardsSelected) {
      // Open in new tab if user already has cards
      window.open(url, '_blank');
    } else {
      // Navigate in current page if no cards selected
      window.location.href = url;
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      {/* Minimal style tag for scrollbar pseudo-elements (can't be inline) */}
      <style>{`
        .wallet-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .wallet-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .wallet-scroll-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .wallet-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>

      <div style={{ padding: isMobile ? '0' : '0 24px', marginBottom: 20 }}>
        {!hasCardsSelected ? (
          <h2
            style={{
              fontFamily: 'Merriweather',
              fontSize: '28px',
              margin: '0 0 8px 0',
              textAlign: 'center',
            }}
          >
            Or start from a sample wallet:
          </h2>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <h2
                style={{
                  fontFamily: 'Merriweather',
                  fontSize: '28px',
                  margin: '0 0 8px 0',
                }}
              >
                Explore other wallets
              </h2>
              <p style={{ color: '#666', margin: 0, fontSize: '16px' }}>
                See how different card combinations work together
              </p>
            </div>
            <Link
              to="/browse-wallets"
              style={{
                textDecoration: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#e5e7eb',
                color: '#1f2937',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#e5e7eb';
              }}
            >
              Browse all
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        )}
      </div>

      <div
        style={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
        }}
      >
        <div
          className="wallet-scroll-container"
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x proximity',
            scrollPaddingLeft: '24px',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
            padding: isMobile ? '0 15px' : '0 24px',
          }}
        >
          {walletTemplates.map((wallet) => (
            <WalletCard
              isMobile={isMobile}
              key={wallet.name}
              wallet={wallet}
              hasCardsSelected={hasCardsSelected}
              onClick={() => handleWalletClick(wallet)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
